// src/pages/stylist/DownPaymentPolicyPage.tsx
// Read-only view of down payment settings (edit on mobile only).
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUI } from '../../context/UIContext';
import api from '../../api/apiClient';
import '../../styles/DownPaymentPolicy.css';

interface DownPaymentPolicy {
  enabled: boolean;
  percentage: number;
  acknowledgedAt?: string | null;
}

const DownPaymentPolicyPage: React.FC = () => {
  const navigate = useNavigate();
  const { showSnackbar } = useUI();

  const [policy, setPolicy] = useState<DownPaymentPolicy>({
    enabled: false,
    percentage: 0,
  });
  const [loading, setLocalLoading] = useState(true);

  const fetchPolicy = useCallback(async () => {
    try {
      setLocalLoading(true);
      const response = await api.get('/profile/down-payment-policy');
      const data = response.data.data;

      setPolicy({
        enabled: data.enabled === true,
        percentage: data.percentage ?? 0,
        acknowledgedAt: data.acknowledgedAt ?? null,
      });
    } catch (error) {
      console.error('[DownPaymentPolicy] Error fetching policy:', error);
      showSnackbar('Failed to load booking guarantee policy', 'error');
    } finally {
      setLocalLoading(false);
    }
  }, [showSnackbar]);

  useEffect(() => {
    fetchPolicy();
  }, [fetchPolicy]);

  if (loading) {
    return (
      <div className="tesla-layout">
        <div className="tesla-loading">
          <div className="tesla-spinner"></div>
          <p>Loading policy...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="tesla-layout down-payment-policy">
      <div className="tesla-section-header">
        <button
          className="tesla-button tesla-button--outline tesla-button--icon-only"
          onClick={() => navigate(-1)}
        >
          <i className="icon-arrow-left"></i>
        </button>
        <h1 className="tesla-title">Booking Guarantee Settings</h1>
      </div>

      <div className="down-payment-policy__content">
        <div className="down-payment-policy__info">
          <i className="icon-shield"></i>
          <p>
            These settings are managed in the LookBel mobile app. Changes you make on mobile
            appear here automatically.
          </p>
        </div>

        <div className="tesla-card">
          <div className="tesla-card__header">
            <i className="icon-cash tesla-card__icon"></i>
            <h2 className="tesla-card__title">Booking Guarantee Policy</h2>
          </div>

          <div className="tesla-toggle-group">
            <div className="tesla-toggle-label">
              <h3>Require booking guarantee</h3>
              <p>{policy.enabled ? 'Enabled' : 'Disabled'}</p>
            </div>
            <span style={{ fontWeight: 600, color: policy.enabled ? '#007AFF' : '#666' }}>
              {policy.enabled ? 'On' : 'Off'}
            </span>
          </div>
        </div>

        {policy.enabled && (
          <div className="tesla-card">
            <div className="tesla-form-group">
              <label className="tesla-label">Booking Guarantee Percentage</label>
              <p style={{ fontSize: 24, fontWeight: 700, margin: '8px 0' }}>{policy.percentage}%</p>
              <p className="tesla-hint">
                Clients may be charged this percentage if they cancel late or don't show up. The full service amount is charged when the appointment is completed.
              </p>
            </div>

            {policy.percentage > 0 && policy.percentage <= 100 && (
              <div className="down-payment-policy__example">
                <i className="icon-info"></i>
                <p>
                  <strong>Example:</strong> For a $100 service, the client&apos;s card is saved now.
                  ${((100 * policy.percentage) / 100).toFixed(2)} may be charged if they cancel late
                  or no-show. $100.00 is charged when the service is completed.
                </p>
              </div>
            )}
          </div>
        )}

        {!policy.enabled && (
          <div className="down-payment-policy__warning">
            <i className="icon-warning"></i>
            <p>
              Booking guarantees are currently off. Clients are not charged if they cancel or no-show.
            </p>
          </div>
        )}

        <div className="down-payment-policy__info">
          <i className="icon-info"></i>
          <p>
            {policy.acknowledgedAt
              ? `Last reviewed: ${new Date(policy.acknowledgedAt).toLocaleDateString()}`
              : 'Not yet reviewed and acknowledged on mobile.'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default DownPaymentPolicyPage;
